import React, { useState, useEffect, useRef } from "react";
import { Document, Page } from "react-pdf";
import { extractCleanSequences } from "../../../../../utilities/textUtils";
import CircularProgress from "@mui/material/CircularProgress";

const performHighlightingInViewer = (textLayerDiv, searchTermsArray) => {
  console.log(
    "[PdfViewer] performHighlightingInViewer called. textLayerDiv:",
    textLayerDiv,
    "searchTermsArray:",
    searchTermsArray
  );
  if (!textLayerDiv) return;

  // Query for spans that react-pdf uses to render text.
  // Using 'span[role="presentation"]' can be more specific if default spans are too broad.
  // For simplicity, starting with 'span'. Adjust if it grabs unintended spans.
  const textSpans = Array.from(textLayerDiv.querySelectorAll("span"));
  console.log("[PdfViewer] Found textSpans count:", textSpans.length);

  // Phase 1: Clear previous highlights by restoring original HTML
  for (const span of textSpans) {
    if (span.dataset.originalHtml) {
      span.innerHTML = span.dataset.originalHtml;
      delete span.dataset.originalHtml;
    }
    // As a fallback, clear direct styles if originalHtml was somehow not used or cleared
    // This is more of a safeguard and might not be strictly necessary if originalHtml logic is robust.
    span.style.backgroundColor = "";
    span.style.color = "";
  }

  const searchText =
    searchTermsArray && searchTermsArray.length > 0
      ? searchTermsArray[0]
      : null;
  console.log("[PdfViewer] searchText for highlighting:", searchText);

  if (!searchText || searchText.trim() === "") {
    return; // No search text or empty search text means highlights are cleared.
  }

  const trimmedSearchText = searchText.trim();
  // Escape special characters for RegExp
  const escapedSearchText = trimmedSearchText.replace(
    /[.*+?^${}()|[\\\]]/g,
    "\\$&"
  );
  const searchRegex = new RegExp(escapedSearchText, "gi"); // 'g' for global, 'i' for case-insensitive

  // Phase 2: Apply new highlights
  for (const span of textSpans) {
    const currentTextContent = span.textContent;
    if (currentTextContent && searchRegex.test(currentTextContent)) {
      console.log(
        `[PdfViewer] Match found for '${searchText}' in span:`,
        span.textContent
      );
      // If this span will be highlighted, ensure its original state is saved.
      // This original state is the HTML before any highlighting was ever applied to this span.
      if (!span.dataset.originalHtml) {
        span.dataset.originalHtml = span.innerHTML;
      }

      // Perform replacement on the *original* HTML (pristine version) to avoid nested highlights.
      const newHtml = span.dataset.originalHtml.replace(
        searchRegex,
        (match) =>
          `<span class="pdf-highlight" style="background-color: yellow; color: black;">${match}</span>`
      );
      span.innerHTML = newHtml;
      console.log("[PdfViewer] Applied highlight. New innerHTML:", newHtml);
    }
  }
};

function PdfViewer({
  selectedPdf,
  pageNumber,
  // numPages, // numPages is not directly used in PdfViewer, can be removed if not needed by parent for other logic
  onDocumentLoadSuccess,
  onDocumentLoadError,
  textLayerRef,
  width,
  citationContent, // Used for initial term extraction
  searchTerms = [], // These are now the debounced search terms from parent
  onSearchTermsChange, // Callback to parent, e.g., for initial term extraction
}) {
  const [searchTermsState, setSearchTermsState] = useState(searchTerms);

  // Track if we've processed the initial citation content
  const initialCitationProcessed = useRef(false);

  // Notify parent of the first sequence when citation content changes
  useEffect(() => {
    // This effect is for extracting an initial search term from citationContent
    // It should run when citationContent changes, and only if no searchTerms are already provided
    if (
      citationContent &&
      onSearchTermsChange &&
      !initialCitationProcessed.current
    ) {
      const sequences = extractCleanSequences(citationContent);
      // Only set initial search term if `searchTermsState` (local state, derived from debounced prop) is empty
      if (
        sequences.length > 0 &&
        (!searchTermsState || searchTermsState.length === 0)
      ) {
        onSearchTermsChange([sequences[0]]); // This might trigger a searchText update in parent
      }
      initialCitationProcessed.current = true;
    }
  }, [citationContent, onSearchTermsChange, searchTermsState]);

  // Update local search terms state when prop changes
  useEffect(() => {
    setSearchTermsState(searchTerms);
  }, [searchTerms]);

  // Reset the initialCitationProcessed flag when citation content changes
  useEffect(() => {
    initialCitationProcessed.current = false;
  }, [citationContent]);

  // Apply custom text layer rendering
  useEffect(() => {
    if (textLayerRef.current) {
      performHighlightingInViewer(textLayerRef.current, searchTermsState);

      // If we have search terms, scroll to the first match
      if (searchTermsState && searchTermsState.length > 0) {
        // Query for our specific highlight class
        const firstMatch =
          textLayerRef.current.querySelector("span.pdf-highlight");
        if (firstMatch) {
          firstMatch.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }
    }
  }, [pageNumber, searchTermsState, width, textLayerRef]);

  // Text layer is now handled by onRenderTextLayerSuccess

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        width: "100%",
        height: "100%",
        //overflow: 'auto',
        padding: "10px",
        boxSizing: "border-box",
        backgroundColor: "#f5f5f5",
        borderRadius: "4px",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-start",
          width: "100%",
          overflow: "auto",
          minHeight: "100%",
          padding: "20px 0",
          boxSizing: "border-box",
        }}
      >
        <Document
          file={selectedPdf}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={onDocumentLoadError}
          loading={
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                padding: "40px 20px",
              }}
            >
              <CircularProgress />
              <p style={{ marginTop: "10px" }}>Loading PDF...</p>
            </div>
          }
          style={{
            width: "100%",
            maxWidth: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Page
            pageNumber={pageNumber}
            width={width}
            inputRef={textLayerRef}
            // customTextRenderer is removed as performHighlightingInViewer handles the entire layer
            loading={
              <div
                style={{
                  textAlign: "center",
                  padding: "40px 20px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                }}
              >
                <CircularProgress />
                <p style={{ marginTop: "10px" }}>
                  Loading page {pageNumber}...
                </p>
              </div>
            }
            style={{
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              backgroundColor: "white",
              maxWidth: "100%",
              height: "auto",
              overflow: "auto",
              margin: "0 auto",
            }}
            renderTextLayer={true}
            onRenderTextLayerSuccess={() => {
              if (textLayerRef.current) {
                performHighlightingInViewer(
                  textLayerRef.current,
                  searchTermsState
                );
              }
            }}
          />
        </Document>
      </div>
    </div>
  );
}

export default PdfViewer;
